/**
 * CertificateViewPage — iter-217c-v17
 *
 * Read-only page that renders a saved certificate exactly the same way
 * CertificateCreatePage renders the preview, then offers:
 *
 *   • APPROVED — a "تحميل PDF" button that re-generates the PDF client-side
 *                (with the snapshotted signature + stamp embedded).
 *   • PENDING  — a warning banner + admin-only "Approve now" shortcut.
 *   • REJECTED — a warning banner with the rejection reason.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award, Loader2, Download, Clock, CheckCircle2, XCircle, ArrowRight,
} from 'lucide-react';
import jsPDF from 'jspdf';
import * as htmlToImage from 'html-to-image';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '../../components/ui/button';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL   = '/pdf-logo-cloudgm.png';

const BRAND = {
  blue:     '#3B9EDC',
  blue700:  '#0369a1',
  orange:   '#F5A55A',
  orange700:'#d97706',
  ink:      '#0f172a',
  ink400:   '#64748b',
  paper:    '#ffffff',
  tint:     '#f8fafc',
  gradient: 'linear-gradient(90deg, #3B9EDC 0%, #F5A55A 100%)',
};

const CONTACT = {
  phone: '+971 50 151 1917',
};

const CATEGORY_META = {
  merchant: { ar: 'منصة التجار',  en: 'Merchants' },
  customer: { ar: 'منصة العملاء', en: 'Customers' },
  company:  { ar: 'للشركة',        en: 'Company' },
  employee: { ar: 'للموظفين',     en: 'Employees' },
};

export default function CertificateViewPage() {
  const { category, id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getAuthHeaders: authHeaders } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const previewRef = useRef(null);

  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const meta = CATEGORY_META[category] || CATEGORY_META.customer;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/admin/certificates/single/${id}`,
          { headers: authHeaders() },
        );
        if (!cancelled) setCert(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || t('فشل تحميل الشهادة', 'Failed to load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, authHeaders]);

  const canDownload = cert?.status === 'approved';

  const downloadPDF = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    try {
      const png = await htmlToImage.toPng(previewRef.current, {
        pixelRatio: 2, cacheBust: true, backgroundColor: BRAND.tint,
      });
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'p' });
      doc.addImage(png, 'PNG', 0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight());
      const safeName = (cert.recipient_name || 'recipient').replace(/[^\p{L}\p{N}]+/gu, '_').slice(0, 60);
      doc.save(`Certificate_${cert.cert_no}_${safeName}.pdf`);
      toast.success(t('تم التحميل', 'Downloaded'));
    } catch (err) {
      toast.error(t('فشل إنشاء PDF', 'PDF failed') + ': ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const statusBanner = useMemo(() => {
    if (!cert) return null;
    if (cert.status === 'approved') {
      return {
        icon: CheckCircle2,
        title: t('شهادة معتمدة — جاهزة للتحميل', 'Certificate approved — ready to download'),
        sub:   t(`اعتمدها ${cert.approved_by || 'الأدمن'} في ${(cert.approved_at || '').slice(0, 10)}`,
                 `Approved by ${cert.approved_by || 'admin'} on ${(cert.approved_at || '').slice(0, 10)}`),
        color: 'emerald',
      };
    }
    if (cert.status === 'rejected') {
      return {
        icon: XCircle,
        title: t('الشهادة مرفوضة', 'Certificate rejected'),
        sub:   cert.reject_reason || t('لا يوجد سبب مسجّل', 'No reason recorded'),
        color: 'rose',
      };
    }
    return {
      icon: Clock,
      title: t('بانتظار اعتماد الأدمن', 'Awaiting admin approval'),
      sub:   t('لن تُعرض هنا التواقيع والختم إلا بعد الاعتماد', 'Signature and stamp will appear here only after approval'),
      color: 'amber',
    };
  }, [cert, t]);

  return (
    <DashboardLayout
      title={`عرض الشهادة — ${meta.ar}`}
      titleEn={`View Certificate — ${meta.en}`}
      subtitle="معاينة الشهادة كما ستُصدر — تحميلها متاح فقط بعد الاعتماد"
      subtitleEn="Certificate preview — download becomes available after approval"
      screenCode="FZLC-CERT-VIEW"
      backPath={`/manage/cloudgm-fz-llc/certificates/${category}`}
      onBack={() => navigate(`/manage/cloudgm-fz-llc/certificates/${category}`)}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'الشهادات', labelEn: 'Certificates', path: '/manage/cloudgm-fz-llc/certificates' },
        { labelAr: meta.ar, labelEn: meta.en, path: `/manage/cloudgm-fz-llc/certificates/${category}` },
        { labelAr: 'عرض', labelEn: 'View' },
      ]}
    >
      <main className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-6" dir={isAr ? 'rtl' : 'ltr'} data-testid="cert-view-page">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/40" /></div>
        ) : !cert ? (
          <div className="p-16 text-center text-white/60">
            {t('لم يتم العثور على الشهادة', 'Certificate not found')}
          </div>
        ) : (
          <>
            {statusBanner && (
              <StatusBanner banner={statusBanner} />
            )}

            {/* Action bar */}
            <div className="flex items-center justify-between gap-3 my-4 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-white/50 font-mono" dir="ltr">
                <Award className="w-3.5 h-3.5" />
                <span>{cert.cert_no}</span>
                <span>·</span>
                <span>{(cert.created_at || '').slice(0, 10)}</span>
              </div>

              {canDownload ? (
                <Button
                  onClick={downloadPDF}
                  disabled={downloading}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-5"
                  data-testid="cert-view-download-btn"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 me-1.5" /> {t('تحميل PDF', 'Download PDF')}</>}
                </Button>
              ) : cert.status === 'pending_approval' ? (
                <Button
                  onClick={() => navigate('/signatures-approvals/certificates')}
                  variant="outline"
                  className="border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 h-11 px-5"
                  data-testid="go-to-approvals-btn"
                >
                  {t('انتقل إلى صفحة الاعتماد', 'Go to approvals page')}
                  <ArrowRight className="w-4 h-4 ms-1.5" />
                </Button>
              ) : null}
            </div>

            {/* Rendered certificate — SAME layout as CertificateCreatePage preview */}
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
              <div className="w-full flex justify-center bg-black/40 p-4 overflow-x-auto">
                <CertificatePreview cert={cert} previewRef={previewRef} />
              </div>
            </div>
          </>
        )}
      </main>
    </DashboardLayout>
  );
}

function StatusBanner({ banner }) {
  const IconC = banner.icon;
  const palette = {
    emerald: { bg: 'bg-emerald-500/10',  border: 'border-emerald-500/30', text: 'text-emerald-100', icon: 'text-emerald-300' },
    rose:    { bg: 'bg-rose-500/10',     border: 'border-rose-500/30',    text: 'text-rose-100',    icon: 'text-rose-300' },
    amber:   { bg: 'bg-amber-500/10',    border: 'border-amber-500/30',   text: 'text-amber-100',   icon: 'text-amber-300' },
  }[banner.color];
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-start gap-3 p-4 rounded-2xl border ${palette.bg} ${palette.border}`}
    >
      <IconC className={`w-5 h-5 mt-0.5 flex-shrink-0 ${palette.icon}`} />
      <div className={palette.text}>
        <div className="font-semibold text-sm">{banner.title}</div>
        <div className="text-[12px] opacity-80 mt-0.5">{banner.sub}</div>
      </div>
    </motion.div>
  );
}

/**
 * CertificatePreview — pure render of the certificate.
 * Keep the DOM shape IDENTICAL to CertificateCreatePage's preview so the
 * downloaded PDF matches exactly.
 */
export function CertificatePreview({ cert, previewRef }) {
  const isTo = cert.cert_type === 'to_whom';
  // iter-218r (Feb 2026) — Apply the typography settings persisted at
  // composition time. Fall back to the pre-iter-218r defaults if the fields
  // are missing (older certs that were saved before this feature).
  const bodyFontSize   = Number(cert.body_font_size)   || 15;
  const bodyLineHeight = Number(cert.body_line_height) || 1.9;
  const _fontMap = {
    Cairo:            '"Cairo","Tajawal",-apple-system,BlinkMacSystemFont,sans-serif',
    Tajawal:          '"Tajawal","Cairo",-apple-system,BlinkMacSystemFont,sans-serif',
    Amiri:            '"Amiri","Cairo","Times New Roman",serif',
    'Noto Naskh':     '"Noto Naskh Arabic","Amiri","Cairo",serif',
    'Times New Roman':'"Times New Roman","Amiri","Cairo",serif',
    Arial:            'Arial,"Cairo","Tajawal",sans-serif',
  };
  const bodyFontStack = _fontMap[cert.body_font_family] || _fontMap.Cairo;
  return (
    <div
      ref={previewRef}
      style={{
        width:  '794px',
        height: '1123px',
        background: BRAND.tint,
        color: BRAND.ink,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: bodyFontStack,
        position: 'relative',
      }}
    >
      {/* Header */}
      <div style={{ padding: '32px 60px 0 60px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ textAlign: 'left', minWidth: 120 }}>
            <div style={{ fontSize: 10, color: BRAND.ink400, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Date</div>
            <div style={{ fontSize: 13, color: BRAND.ink, fontWeight: 700, fontFamily: 'monospace', marginTop: 2 }} dir="ltr">{cert.cert_date}</div>
          </div>
          <img src={LOGO_URL} alt="CloudGM" style={{ height: 90, objectFit: 'contain' }} crossOrigin="anonymous" />
          <div style={{ textAlign: 'right', minWidth: 120 }}>
            <div style={{ fontSize: 10, color: BRAND.ink400, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Cert #</div>
            <div style={{ fontSize: 13, color: BRAND.ink, fontWeight: 700, fontFamily: 'monospace', marginTop: 2 }} dir="ltr">{cert.cert_no}</div>
          </div>
        </div>
        <div style={{ height: 5, background: BRAND.gradient, borderRadius: 3, marginTop: 20 }} />
      </div>

      {/* Title (only for to_whom) */}
      {isTo && (
        <div style={{ padding: '32px 60px 8px 60px', textAlign: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
            <div dir="ltr" style={{ minHeight: 42 }}>
              <div style={{ fontSize: 12, color: BRAND.ink400, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 600 }}>To Whom It May Concern</div>
              <div style={{ marginTop: 8, height: 3, width: 100, background: BRAND.orange }} />
            </div>
            <div dir="rtl" style={{ minHeight: 42 }}>
              <div style={{ fontSize: 22, color: BRAND.blue700, fontWeight: 800 }}>لمن يهمه الأمر</div>
              <div style={{ marginTop: 8, height: 3, width: 100, background: BRAND.orange, marginRight: 0, marginLeft: 'auto' }} />
            </div>
          </div>
        </div>
      )}

      {/* Body: 2 columns — iter-218l: reduced top padding to grant more
          vertical room for long congratulation/TWMC bodies. */}
      <div style={{
        padding: isTo ? '14px 60px 0 60px' : '28px 60px 0 60px',
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 4px 1fr',
        gap: 32,
      }}>
        {/* English */}
        <div dir="ltr" style={{ fontSize: bodyFontSize, lineHeight: bodyLineHeight, color: BRAND.ink, textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: 16, marginBottom: 6 }}>
            {cert.cert_type === 'congratulation' && (
              <div style={{ fontSize: 13, color: BRAND.blue700, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>Congratulations</div>
            )}
          </div>
          {/* iter-218l: compact recipient line — "To Mr./Mrs. NAME" instead
              of the bulky Recipient box. */}
          <div style={{
            fontSize: 14, color: BRAND.ink, marginBottom: 12,
            display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap',
          }}>
            <span style={{ color: BRAND.ink400, fontWeight: 600 }}>To</span>
            {cert.recipient_title_en && (
              <span style={{ color: BRAND.ink, fontWeight: 700 }}>{cert.recipient_title_en}</span>
            )}
            <span style={{ color: BRAND.orange700, fontWeight: 800, fontSize: 16 }}>
              {(cert.recipient_name_en || cert.recipient_name) || '—'}
            </span>
          </div>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: bodyLineHeight }}>{cert.body_en}</p>
        </div>

        {/* Divider */}
        <div style={{ background: BRAND.gradient, borderRadius: 2 }} />

        {/* Arabic */}
        <div dir="rtl" style={{ fontSize: bodyFontSize + 1, lineHeight: bodyLineHeight, color: BRAND.ink, textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: 16, marginBottom: 6 }}>
            {cert.cert_type === 'congratulation' && (
              <div style={{ fontSize: 14, color: BRAND.blue700, fontWeight: 700, letterSpacing: 1 }}>تهنئة</div>
            )}
          </div>
          <div style={{
            fontSize: 15, color: BRAND.ink, marginBottom: 12,
            display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap',
          }}>
            <span style={{ color: BRAND.ink400, fontWeight: 600 }}>إلى</span>
            {cert.recipient_title_ar && (
              <span style={{ color: BRAND.ink, fontWeight: 700 }}>{cert.recipient_title_ar}</span>
            )}
            <span style={{ color: BRAND.orange700, fontWeight: 800, fontSize: 17 }}>
              {(cert.recipient_name_ar || cert.recipient_name) || '—'}
            </span>
          </div>
          <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: bodyLineHeight }}>{cert.body_ar}</p>
        </div>
      </div>

      {/* Signature strip — iter-218j (Feb 2026):
            Two-column horizontal layout matching user's mockup:
              LEFT column: signature → dashed underline → AR name + EN name
                           → AR title + EN title → AR company + EN company
              RIGHT column: LARGE company stamp (~200×200px)
            Both columns bottom-aligned so the stamp sits at the same baseline
            as the company-name block. Falls back gracefully to single-side
            rendering when signature or stamp is missing. */}
      <div dir="ltr" style={{
        padding: '32px 60px 0 60px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        gap: 24,
      }}>
        {/* LEFT: signature + printed name + title + company */}
        <div style={{ flex: '0 0 auto', minWidth: 240, maxWidth: 380 }}>
          {/* Signature image (script style) — iter-218l: enlarged */}
          {cert.signature_data_url ? (
            <img
              src={cert.signature_data_url}
              alt="signature"
              style={{
                display: 'block',
                maxHeight: 110, maxWidth: 300, objectFit: 'contain',
                marginBottom: 2,
              }}
              crossOrigin="anonymous"
            />
          ) : (
            <div style={{ height: 60 }} />
          )}

          {/* Dashed underline separator */}
          <div style={{
            borderTop: `1.5px dashed ${BRAND.ink400}`,
            width: '80%', maxWidth: 240, marginBottom: 8,
          }} />

          {/* Printed name — Arabic + English (Arabic first per user's mockup) */}
          {(cert.signer_name_ar || cert.signer_name_en) && (
            <div style={{ marginBottom: 6 }}>
              {cert.signer_name_ar && (
                <div dir="rtl" style={{
                  fontSize: 15, color: BRAND.ink, fontWeight: 800,
                  lineHeight: 1.35, textAlign: 'left',
                }}>
                  {cert.signer_name_ar}
                </div>
              )}
              {cert.signer_name_en && (
                <div dir="ltr" style={{
                  fontSize: 14, color: BRAND.ink, fontWeight: 700,
                  lineHeight: 1.35,
                }}>
                  {cert.signer_name_en}
                </div>
              )}
            </div>
          )}

          {/* Job title — Arabic + English */}
          {(cert.signer_title_ar || cert.signer_title_en) && (
            <div style={{ marginBottom: 8 }}>
              {cert.signer_title_ar && (
                <div dir="rtl" style={{
                  fontSize: 13, color: BRAND.ink400, fontWeight: 700,
                  lineHeight: 1.35, textAlign: 'left',
                }}>
                  {cert.signer_title_ar}
                </div>
              )}
              {cert.signer_title_en && (
                <div dir="ltr" style={{
                  fontSize: 12, color: BRAND.ink400, fontWeight: 600,
                  lineHeight: 1.35,
                }}>
                  {cert.signer_title_en}
                </div>
              )}
            </div>
          )}

          {/* Company name — English on top, Arabic below (matching mockup) */}
          <div style={{ paddingTop: 4 }}>
            <div dir="ltr" style={{
              fontSize: 14, color: BRAND.ink, fontWeight: 800,
              letterSpacing: 0.4, lineHeight: 1.35,
            }}>
              {cert.company_name_en || 'CLOUDGM FZ-LLC'}
            </div>
            {cert.company_name_ar && (
              <div dir="rtl" style={{
                fontSize: 13, color: BRAND.ink, fontWeight: 700,
                lineHeight: 1.35, textAlign: 'left',
              }}>
                {cert.company_name_ar}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: large official company stamp */}
        <div style={{
          flex: '0 0 auto', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'flex-end',
        }}>
          {cert.stamp_data_url && (
            <img
              src={cert.stamp_data_url}
              alt="official company stamp"
              style={{
                maxHeight: 160, maxWidth: 170, objectFit: 'contain',
                opacity: 0.95,
              }}
              crossOrigin="anonymous"
            />
          )}
        </div>
      </div>

      {/* Footer — iter-218 (Feb 2026):
            - Website icon next to sites
            - Mail icon next to emails
            - WhatsApp icon next to phone
            - Phone shown as a single tight literal (no extra letter-spacing) */}
      <div style={{
        marginTop: 28, background: BRAND.gradient, color: '#fff',
        padding: '14px 60px', fontSize: 12, fontWeight: 600,
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr', gap: 20, alignItems: 'center' }} dir="ltr">
          {/* Sites */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.5 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
              </svg>
              cloudgm.com
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
              </svg>
              cloudgm.business
            </span>
          </div>
          {/* Phone with WhatsApp icon */}
          <div style={{
            textAlign: 'center',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: 0,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" style={{ flexShrink: 0 }}>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
            </svg>
            {CONTACT.phone}
          </div>
          {/* Emails */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right', lineHeight: 1.5 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 5L2 7" />
              </svg>
              info@cloudgm.com
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 5L2 7" />
              </svg>
              info@cloudgm.business
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
